import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../app/layout/PageHeader";
import "../../css/tables.css";

interface ArchivedParty {
  partyId: number | null;
  householdId: number | null;
  displayName: string;
  partyType: string;
  lastUpdatedDate: string | null;
  serviceNames: string[];
}
type SortColumn = "displayName" | "partyType" | "services" | "lastUpdatedDate";

export default function ArchivedPartiesReportPage() {
  const { selectedTenant } = useAuth();
  const [rows, setRows] = useState<ArchivedParty[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [service, setService] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("lastUpdatedDate");
  const [ascending, setAscending] = useState(false);
  useEffect(() => {
    let current = true;
    setRows([]); setLoading(true); setFailed(false);
    api<ArchivedParty[]>("/api/reports/archived-parties")
      .then(result => { if (current) setRows(result); })
      .catch(() => { if (current) setFailed(true); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [selectedTenant?.tenantId]);

  const filtered = rows.filter(row => (!type || row.partyType === type) &&
    (!service || row.serviceNames.includes(service)) &&
    `${row.displayName} ${row.partyType} ${row.serviceNames.join(" ")}`.toLowerCase().includes(search.trim().toLowerCase())
  ).sort((a, b) => {
    const value = (row: ArchivedParty) => sortColumn === "services" ? row.serviceNames.join(", ") : (row[sortColumn] ?? "");
    return value(a).localeCompare(value(b), "en-GB", { numeric: true, sensitivity: "base" }) * (ascending ? 1 : -1);
  });
  const heading = (column: SortColumn, label: string) => <th scope="col" aria-sort={sortColumn === column ? (ascending ? "ascending" : "descending") : "none"}>
    <button type="button" className="app-table-sort" onClick={() => { setAscending(sortColumn === column ? !ascending : true); setSortColumn(column); }}>
      {label}<span aria-hidden="true">{sortColumn === column ? (ascending ? "↑" : "↓") : "↕"}</span>
    </button>
  </th>;

  return <div className="page-container">
    <PageHeader title="Archived Parties" subtitle="Archived records linked to your organisation. Archiving a family preserves its individual people."
      actions={<Link className="link-button" to="/reports">Back to Report Centre</Link>} />
    <div className="card">
      <div className="app-table-filters">
        <div><label className="form-label" htmlFor="archived-search">Search archived parties</label>
          <input className="form-input" id="archived-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search names or services" /></div>
        <div><label className="form-label" htmlFor="archived-type">Party type</label>
          <select className="form-input" id="archived-type" value={type} onChange={e => setType(e.target.value)}>
            <option value="">All types</option><option>Person</option><option>Family</option><option>Organisation</option>
          </select></div>
        <div><label className="form-label" htmlFor="archived-service">Previous service</label>
          <select className="form-input" id="archived-service" value={service} onChange={e => setService(e.target.value)}>
            <option value="">All services</option>{Array.from(new Set(rows.flatMap(row => row.serviceNames))).sort().map(name => <option key={name}>{name}</option>)}
          </select></div>
      </div>
      {(search || type || service) && <button type="button" className="link-button" onClick={() => { setSearch(""); setType(""); setService(""); }}>Clear filters</button>}
      <p className="field-hint" aria-live="polite">{loading ? "Loading archived parties..." : failed ? "Unable to load archived parties. Refresh the page to try again." : `${filtered.length} of ${rows.length} archived records shown.`}</p>
      <div className="app-table-container"><table className="app-data-table" aria-label="Archived parties">
        <thead><tr>{heading("displayName", "Party")}{heading("partyType", "Type")}{heading("services", "Previous services")}{heading("lastUpdatedDate", "Last updated")}</tr></thead>
        <tbody>{filtered.map(row => <tr key={row.partyId != null ? `party-${row.partyId}` : `family-${row.householdId}`}>
          <td>{row.displayName}</td><td>{row.partyType}</td><td>{row.serviceNames.join(", ") || "—"}</td>
          <td>{row.lastUpdatedDate ? new Date(row.lastUpdatedDate).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
        </tr>)}</tbody>
      </table></div>
      {!loading && !failed && filtered.length === 0 && <p>{rows.length ? "No archived parties match your filters." : "No archived parties for this organisation."}</p>}
    </div>
  </div>;
}
